// Typed tracking-plan SDK (github.com/AsthaBhopal/analytics_schema) wired to
// the existing RudderStack transport.
//
// Every event goes through the generated, compiler-checked methods on
// `analytics` — one method per event, enums for every governed value,
// payload null-filled to the full dictionary and stamped with
// schema_version. There is no other way to emit an event.
//
// Adding an event or enum value = PR to the analytics_schema repo (one-line
// proto edit; codegen + release are automated), then bump the package here.
import { RupeezyAnalytics } from "@asthabhopal/tracking-plan";
import { currentDeviceType, rudderIdentify, rudderTrack } from "./rudder";
import store from "../store";

// Which Rupeezy app emitted the event (all apps share one RudderStack source +
// domain, so events are otherwise indistinguishable) — same value rudder.ts
// stamps on the RudderStack `context.app` field.
const APP_NAME = "rupeezy_login";
// Build commit — injected by buildspec.yaml as REACT_APP_COMMIT_VERSION.
const APP_VERSION = process.env.REACT_APP_COMMIT_VERSION || "";

// The redux state is loosely typed at this boundary; narrow the one path we
// read. This module sits in an import cycle (store → authReducer →
// trackingPlan → store), so `store` may not be initialised yet when the
// module-level `analytics` construction below runs — hence the try/catch.
function currentApplicationId(): string | undefined {
  try {
    const state = store.getState() as {
      authUser?: { data?: { applicationId?: string } };
    };
    return state.authUser?.data?.applicationId;
  } catch {
    return undefined;
  }
}

function currentContext() {
  return {
    appName: APP_NAME,
    deviceType: currentDeviceType(),
    appVersion: APP_VERSION,
    applicationId: currentApplicationId(),
  };
}

/** Typed tracking surface — one generated method per event in the plan. */
export const analytics = new RupeezyAnalytics({
  track: rudderTrack,
  context: currentContext(),
});

/**
 * Binds the current user to the analytics session (RudderStack identify).
 * Logout resets identity via rudderReset() (auth slices, axios 401 handler).
 */
export function identify(userId: string, traits: Record<string, unknown> = {}): void {
  if (!userId) return;
  rudderIdentify(userId, traits);
}

// Keep application_id (and the browser-computed device type) in the global
// context in step with auth state, refreshed once per auth change. Deferred a
// microtask so the store module has finished initialising (import cycle above).
let lastApplicationId: string | undefined;
if (typeof window !== "undefined") {
  queueMicrotask(() => {
    lastApplicationId = currentApplicationId();
    if (lastApplicationId !== undefined) analytics.updateContext(currentContext());
    store.subscribe(() => {
      const applicationId = currentApplicationId();
      if (applicationId !== lastApplicationId) {
        lastApplicationId = applicationId;
        analytics.updateContext(currentContext());
      }
    });
  });
}

// String -> enum conversion lives in the SDK as generated per-enum parsers
// (sourceFromWire, screenFromWire, ...) — import them from
// @asthabhopal/tracking-plan directly. The one app-side helper below covers
// free-form label munging, which the parsers deliberately don't.

/** Snake-cases free-form label text ("Trending News" -> "trending_news"). */
export function snake(v: unknown): string | undefined {
  const raw = String(v ?? "").trim();
  if (!raw) return undefined;
  return raw
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2") // camelCase -> camel_Case
    .replace(/[\s.,/-]+/g, "_")
    .toLowerCase()
    .replace(/_(tap|tapped|click|clicked|view|viewed|launch|btn|button)$/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}
